/**
 * «Финансы → Статьи CRM»: какая НАША статья P&L стоит за статьёй CRM
 * (`sFinance_transaction_group`). Большинство сопоставлено само — по типу и
 * статье CRM; здесь правят остальное. Ручное сопоставление загрузка не трогает;
 * «авто» возвращает автоматическое.
 *
 * Сверху — статьи, по которым есть выплаты касс: они влияют на отчёт.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router";

import { Combobox, groupOptions } from "@/components/Combobox";
import { NoAccess } from "@/components/NoAccess";
import { KASSA, KASSA_EDIT, useCan } from "@/lib/access";
import { type CrmArticle, loadCrmArticles, setCrmArticle } from "@/lib/kassa";
import { type Article, articleOptions, loadArticles, money } from "@/lib/pnl";

const TD = "border-b border-border px-2.5 py-1.5 align-top";
const TH = "border-b border-border px-2.5 py-2 text-left";

export function CrmArticlesPage() {
  return useCan(KASSA) ? <CrmArticlesScreen /> : <NoAccess what="статьям CRM" />;
}

function CrmArticlesScreen() {
  const canEdit = useCan(KASSA_EDIT);
  const [rows, setRows] = useState<CrmArticle[] | null>(null);
  const [articles, setArticles] = useState<Article[]>([]);
  const [onlyUsed, setOnlyUsed] = useState(true);
  const [onlyUnmapped, setOnlyUnmapped] = useState(false);
  const [failure, setFailure] = useState("");

  useEffect(() => {
    loadCrmArticles()
      .then(setRows)
      .catch((error: Error) => setFailure(error.message));
    loadArticles()
      .then(setArticles)
      .catch(() => setArticles([])); // статьи P&L — право «P&L»; без него только смотреть
  }, []);

  const change = (row: CrmArticle, articleId: number | null) => {
    setFailure("");
    setCrmArticle(row.id, articleId)
      .then((fresh) => setRows((prev) => prev?.map((r) => (r.id === fresh.id ? fresh : r)) ?? prev))
      .catch((error: Error) => setFailure(error.message));
  };

  const options = articleOptions(articles).map((g) => ({
    ...g,
    items: g.items.filter((a) => a.code !== "unassigned"),
  }));
  const shown = (rows ?? []).filter((r) => (!onlyUsed || r.expenses > 0) && (!onlyUnmapped || r.article_id === null));
  const unmapped = (rows ?? []).filter((r) => r.expenses > 0 && r.article_id === null).length;

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4 [&>*]:flex-none">
      <div className="flex flex-wrap items-center gap-3 text-[12.5px] text-fg-muted">
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={onlyUsed} onChange={(e) => setOnlyUsed(e.target.checked)} />
          только с выплатами
        </label>
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={onlyUnmapped} onChange={(e) => setOnlyUnmapped(e.target.checked)} />
          только не сопоставленные
        </label>
        {rows ? (
          <span className={unmapped ? "text-warn" : "text-fg-faint"}>
            не сопоставлено статей с выплатами: {unmapped}
          </span>
        ) : null}
      </div>

      {failure ? (
        <div role="alert" className="rounded-[var(--radius-ui)] border border-err/30 bg-err-bg px-3 py-2 text-[12.5px] text-err">
          {failure}
        </div>
      ) : null}

      {rows === null ? (
        <div className="text-[12.5px] text-fg-muted">…</div>
      ) : rows.length === 0 ? (
        <div className="text-[12.5px] text-fg-muted">
          Статей CRM ещё нет — их приносит загрузка на экране{" "}
          <Link to="/kassa" className="text-accent-text hover:underline">
            «Кассы»
          </Link>
          .
        </div>
      ) : (
        <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
          <table className="w-full border-collapse text-[12.5px]">
            <thead>
              <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
                <th className={TH}>статья CRM</th>
                <th className={`${TH} text-right`}>выплат</th>
                <th className={`${TH} text-right`}>сумма</th>
                <th className={TH}>наша статья</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => (
                <tr key={row.id} className="hover:bg-bg-hover">
                  <td className={TD}>
                    <div className={row.archived ? "text-fg-faint line-through" : ""}>{row.name}</div>
                    <div className="text-[10.5px] text-fg-faint">CRM №{row.crm_id}</div>
                  </td>
                  <td className={`${TD} text-right tabular-nums`}>
                    {row.expenses ? (
                      <Link to={`/kassa?crm_article_id=${row.id}`} className="text-accent-text hover:underline">
                        {row.expenses}
                      </Link>
                    ) : (
                      <span className="text-fg-faint">—</span>
                    )}
                  </td>
                  <td className={`${TD} whitespace-nowrap text-right tabular-nums`}>{row.expenses ? money(row.sum) : ""}</td>
                  <td className={`${TD} min-w-[280px]`}>
                    {canEdit && articles.length ? (
                      <div className="flex items-center gap-2">
                        <Combobox
                          size="sm"
                          className="w-full"
                          warn={row.article_id === null && row.expenses > 0}
                          value={row.article_id ? String(row.article_id) : ""}
                          onChange={(v) => change(row, v ? Number(v) : null)}
                          options={[{ value: "", label: "— не сопоставлена (авто) —" }, ...groupOptions(options)]}
                        />
                        {row.manual ? (
                          <button
                            type="button"
                            title="Вернуть автоматическое сопоставление"
                            onClick={() => change(row, null)}
                            className="whitespace-nowrap text-[11px] text-accent-text hover:underline"
                          >
                            авто
                          </button>
                        ) : (
                          <span className="whitespace-nowrap text-[11px] text-fg-faint">авто</span>
                        )}
                      </div>
                    ) : (
                      <span className={row.article_id === null ? "text-warn" : ""}>
                        {row.article_name ?? "не сопоставлена"}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
